<script setup>
import { useSlots } from 'vue';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import BackButton from '../../../../components/widgets/BackButton.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const slots = useSlots();

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);
</script>

<template>
  <header
    class="flex w-full flex-col items-start border-b border-auth-border pb-5 dark:border-auth-border-dark"
  >
    <BackButton
      v-if="backButtonLabel"
      compact
      :button-label="backButtonLabel"
      class="mb-3"
    />
    <div
      v-if="title"
      class="flex min-h-8 w-full flex-wrap items-center justify-between gap-3"
    >
      <slot name="title">
        <h1
          class="text-xl font-semibold tracking-tight text-auth-text dark:text-auth-text-dark"
        >
          {{ title }}
        </h1>
      </slot>
    </div>
    <div
      v-if="description || $slots.description || linkText || helpURL"
      class="flex w-full max-w-[75ch] flex-col gap-1.5 text-auth-muted dark:text-auth-muted-dark"
    >
      <p
        v-if="description || $slots.description"
        class="mb-0 max-w-3xl text-body-main line-clamp-5 sm:line-clamp-none"
      >
        <slot name="description">{{ description }}</slot>
      </p>
      <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
        <a
          v-if="helpURL && linkText"
          :href="helpURL"
          target="_blank"
          rel="noopener noreferrer"
          class="mb-1 hidden w-fit items-center gap-1 text-sm font-medium text-auth-action hover:text-auth-action-hover hover:underline dark:text-auth-accent dark:hover:text-auth-accent-hover sm:inline-flex"
        >
          {{ linkText }}
          <Icon icon="i-lucide-chevron-right" class="size-4 flex-shrink-0" />
        </a>
      </CustomBrandPolicyWrapper>
    </div>
  </header>
  <div
    v-if="searchPlaceholder || slots.actions || slots.tabs"
    class="mt-4 flex min-w-0 flex-wrap items-center justify-between gap-3"
  >
    <div
      v-if="slots.tabs || searchPlaceholder"
      class="flex min-w-0 flex-1 items-center gap-3"
      :class="{
        'hidden sm:flex': !slots.tabs,
      }"
    >
      <slot name="tabs" />
      <Input
        v-if="searchPlaceholder"
        v-model="searchQuery"
        :placeholder="searchPlaceholder"
        class="group hidden w-64 min-w-0 sm:flex [&>input]:!rounded-xl [&>input]:!border-auth-border [&>input]:!bg-auth-surface [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 dark:[&>input]:!border-auth-border-dark dark:[&>input]:!bg-auth-surface-dark"
        size="sm"
        type="search"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute top-1/2 size-3.5 -translate-y-1/2 text-auth-muted group-focus-within:text-auth-action dark:text-auth-muted-dark dark:group-focus-within:text-auth-accent ltr:left-2.5 rtl:right-2.5"
          />
        </template>
      </Input>
    </div>
    <div
      class="flex shrink-0 items-center gap-3"
      :class="{ 'flex-row-reverse sm:flex-row': !slots.tabs }"
    >
      <slot name="count" />
      <div
        v-if="slots.count && slots.actions"
        class="h-4 w-px flex-shrink-0 bg-auth-border dark:bg-auth-border-dark ltr:ml-1 ltr:mr-2 rtl:ml-2 rtl:mr-1"
      />
      <slot name="actions" />
    </div>
  </div>
</template>
